/* 
* Copyright (c) 2026 Send2Me, media capture and delivery platform. All rights reserved. 
* Distributed under the TruVideo proprietary license. 
*/
import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  PlusCircle,
  Shield,
  Key,
  Layers,
  Tag
} from 'lucide-react';
import { roleService } from '../services/roleService';
import toastService from '../utils/toastService';
import PermissionGrid from '../components/PermissionGrid';
import { useAuth } from '../context/AuthContext';
import { ACCOUNT_LABEL, SUB_ACCOUNT_LABEL } from '../constants/entityLabels';

const ROLES_ROUTE = '/roles';

const SCOPE_OPTIONS = [
  { val: 'platform', label: 'Platform Wide', desc: `Full access across all platform entities and ${SUB_ACCOUNT_LABEL.pluralLower}` },
  { val: 'account', label: `${ACCOUNT_LABEL.singular} Level`, desc: `Scoped to ${ACCOUNT_LABEL.singularLower} management and ${SUB_ACCOUNT_LABEL.pluralLower}` },
  { val: 'sub_account', label: `${SUB_ACCOUNT_LABEL.singular} Level`, desc: `Scoped only to specific ${SUB_ACCOUNT_LABEL.singularLower} operations` },
];

const NewRole = () => {
  const navigate = useNavigate();
  const { userScope } = useAuth();

  const [submitting, setSubmitting] = useState(false);
  const [permissions, setPermissions] = useState([]);
  const [loading, setLoading] = useState(true);

  const currentUserScope = userScope || 'platform';
  const [form, setForm] = useState({
    name: '',
    displayName: '',
    scope: currentUserScope,
    isDefault: false,
    permissionIds: [],
  });

  const [fieldErrors, setFieldErrors] = useState({});

  const availableScopes = useMemo(() => {
    if (currentUserScope === 'platform') return ['platform', 'account', 'sub_account'];
    if (currentUserScope === 'account') return ['account', 'sub_account'];
    return ['sub_account'];
  }, [currentUserScope]);

  const filteredPermissions = useMemo(() => {
    const excludedKeys = [];

    if (form.scope === 'account') {
      excludedKeys.push('accounts.create', 'accounts.update', 'accounts.delete');
    } else if (form.scope === 'sub_account') {
      excludedKeys.push(
        'accounts.create', 'accounts.update', 'accounts.delete',
        'sub_accounts.create', 'sub_accounts.update', 'sub_accounts.delete'
      );
    }

    return permissions.filter(p => !excludedKeys.includes(p.key));
  }, [permissions, form.scope]);

  useEffect(() => {
    const fetchPermissions = async () => {
      try {
        setLoading(true);
        const res = await roleService.getPermissions();
        const permData = res.data?.data || res.data || [];
        setPermissions(permData);
      } catch {
        toastService.error('Failed to load permissions');
      } finally {
        setLoading(false);
      }
    };
    fetchPermissions();
  }, []);

  const validateForm = () => {
    const errors = {};
    if (!form.name.trim()) { errors.name = 'Role key is required'; }
    if (!form.displayName.trim()) { errors.displayName = 'Display name is required'; }
    if (form.permissionIds.length === 0) { errors.permissionIds = 'Select at least one permission'; }

    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleBlur = (field, value) => {
    if (!value || (typeof value === 'string' && !value.trim())) {
      const labels = {
        name: 'Role key',
        displayName: 'Display name',
        permissionIds: 'Permissions',
      };
      setFieldErrors(prev => ({
        ...prev,
        [field]: `${labels[field] || field} is required`,
      }));
    } else {
      setFieldErrors(prev => ({ ...prev, [field]: null }));
    }
  };

  const handleScopeChange = (newScope) => {
    // When scope changes, filter out incompatible permission IDs
    const excludedKeys = [];
    if (newScope === 'account') {
      excludedKeys.push('accounts.create', 'accounts.update', 'accounts.delete');
    } else if (newScope === 'sub_account') {
      excludedKeys.push(
        'accounts.create', 'accounts.update', 'accounts.delete',
        'sub_accounts.create', 'sub_accounts.update', 'sub_accounts.delete'
      );
    }

    setForm(f => ({
      ...f,
      scope: newScope,
      permissionIds: f.permissionIds.filter(permId => {
        const p = permissions.find(item => item.id === permId || item.key === permId);
        return p && !excludedKeys.includes(p.key);
      }),
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) { return; }

    try {
      setSubmitting(true);
      await roleService.createRole({
        name: form.name.trim(),
        displayName: form.displayName.trim(),
        scope: form.scope,
        isDefault: form.isDefault,
        permissionIds: form.permissionIds,
      });

      toastService.success('Role created successfully');
      navigate(ROLES_ROUTE);
    } catch (err) {
      const msg = err.response?.data?.message;
      const errorMsg = Array.isArray(msg) ? msg[0] : (msg || 'Failed to create role');
      toastService.error(errorMsg);
    } finally {
      setSubmitting(false);
    }
  };

  const inputClass = (field) =>
    `w-full px-4 py-2.5 bg-base-100 border rounded-xl text-sm text-base-content placeholder:text-base-content/30 focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-all ${
      fieldErrors[field] ? 'border-error' : 'border-base-300'
    }`;

  const selectClass = (field) =>
    `w-full px-4 py-2.5 bg-base-100 border rounded-xl text-sm text-base-content focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-all font-medium ${
      fieldErrors[field] ? 'border-error' : 'border-base-300'
    }`;

  if (loading) {
    return (
      <div className="space-y-6 animate-pulse p-2 w-full">
        <div className="h-6 w-48 bg-base-300 rounded-lg"></div>
        <div className="glass-card rounded-2xl p-6 h-36 bg-base-100/60"></div>
        <div className="glass-card rounded-2xl p-6 h-48 bg-base-100/60"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-1 sm:p-2 w-full pb-12">
      {/* Top Breadcrumb */}
      <div>
        <button
          type="button"
          onClick={() => navigate(ROLES_ROUTE)}
          className="inline-flex items-center gap-2 text-xs font-black uppercase tracking-widest text-base-content/50 hover:text-primary transition-colors group"
        >
          <ArrowLeft size={14} className="group-hover:-translate-x-1 transition-transform" />
          Back to Roles
        </button>
      </div>

      {/* Hero Header Banner */}
      <div className="glass-card rounded-2xl p-5 sm:p-6 shadow-sm border border-base-content/10">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div className="space-y-1">
            <div className="flex items-center gap-2">
              <span className="text-[10px] font-black uppercase tracking-widest text-base-content/40">
                New Record
              </span>
            </div>
            <h1 className="text-2xl sm:text-3xl font-black text-base-content tracking-tight">
              Create Role
            </h1>
            <p className="text-xs text-base-content/50">
              Define role identifiers, scope level, and assign granular system permissions.
            </p>
          </div>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Role Identity & Scope Card */}
        <div className="glass-card rounded-2xl p-5 sm:p-6 shadow-sm border border-base-content/10 space-y-4">
          <div className="flex items-center gap-2 pb-3 border-b border-base-content/10">
            <Shield size={18} className="text-primary" />
            <h2 className="text-base font-black text-base-content uppercase tracking-wider">
              Role Details & Scope
            </h2>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {/* Role Key (Name) */}
            <div className="space-y-1.5">
              <label htmlFor="name" className="text-[10px] font-black text-base-content/50 uppercase tracking-widest flex items-center gap-1.5">
                <Tag size={12} className="text-primary" />
                <span>Role Key *</span>
              </label>
              <input
                id="name"
                type="text"
                maxLength={50}
                placeholder="e.g. CUSTOM_OPERATOR"
                className={`${inputClass('name')} font-mono uppercase`}
                value={form.name}
                onBlur={(e) => handleBlur('name', e.target.value)}
                onChange={(e) => {
                  const val = e.target.value.toUpperCase().replace(/\s+/g, '_');
                  setForm(f => ({ ...f, name: val }));
                  if (fieldErrors.name) setFieldErrors(prev => ({ ...prev, name: null }));
                }}
              />
              {fieldErrors.name ? (
                <p className="text-error text-xs font-medium">{fieldErrors.name}</p>
              ) : (
                <p className="text-[10px] text-base-content/40">Unique system key identifier</p>
              )}
            </div>

            {/* Display Name */}
            <div className="space-y-1.5">
              <label htmlFor="displayName" className="text-[10px] font-black text-base-content/50 uppercase tracking-widest flex items-center gap-1.5">
                <Shield size={12} className="text-primary" />
                <span>Display Name *</span>
              </label>
              <input
                id="displayName"
                type="text"
                maxLength={50}
                placeholder="e.g. Custom Operator"
                className={inputClass('displayName')}
                value={form.displayName}
                onBlur={(e) => handleBlur('displayName', e.target.value)}
                onChange={(e) => {
                  setForm(f => ({ ...f, displayName: e.target.value }));
                  if (fieldErrors.displayName) setFieldErrors(prev => ({ ...prev, displayName: null }));
                }}
              />
              {fieldErrors.displayName && (
                <p className="text-error text-xs font-medium">{fieldErrors.displayName}</p>
              )}
            </div>

            {/* Scope */}
            <div className="space-y-1.5 sm:col-span-2 lg:col-span-1">
              <label htmlFor="scope" className="text-[10px] font-black text-base-content/50 uppercase tracking-widest flex items-center gap-1.5">
                <Layers size={12} className="text-primary" />
                <span>Scope *</span>
              </label>
              <select
                id="scope"
                className={selectClass('scope')}
                value={form.scope}
                onChange={(e) => handleScopeChange(e.target.value)}
              >
                {SCOPE_OPTIONS.filter(opt => availableScopes.includes(opt.val)).map(opt => (
                  <option key={opt.val} value={opt.val}>
                    {opt.label}
                  </option>
                ))}
              </select>
              <p className="text-[10px] text-base-content/40">
                {SCOPE_OPTIONS.find(o => o.val === form.scope)?.desc}
              </p>
            </div>
          </div>
        </div>

        {/* Permissions Matrix Card */}
        <div className="glass-card rounded-2xl p-5 sm:p-6 shadow-sm border border-base-content/10 space-y-4">
          <div className="flex items-center justify-between pb-3 border-b border-base-content/10 flex-wrap gap-2">
            <div className="flex items-center gap-2">
              <Key size={18} className="text-primary" />
              <h2 className="text-base font-black text-base-content uppercase tracking-wider">
                Assign Permissions
              </h2>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-xs font-bold px-2.5 py-1 rounded-lg bg-primary/10 text-primary border border-primary/20">
                {form.permissionIds.length} Selected
              </span>
            </div>
          </div>

          <PermissionGrid
            isLoading={loading}
            permissions={filteredPermissions}
            selectedIds={form.permissionIds}
            onChange={(newIds) => {
              setForm(f => ({ ...f, permissionIds: newIds }));
              if (fieldErrors.permissionIds && newIds.length > 0) {
                setFieldErrors(prev => ({ ...prev, permissionIds: null }));
              }
            }}
          />

          {fieldErrors.permissionIds && (
            <p className="text-error text-xs font-medium mt-2">{fieldErrors.permissionIds}</p>
          )}
        </div>

        {/* Form Actions Footer */}
        <div className="flex flex-col-reverse sm:flex-row items-center gap-3 pt-2">
          <button
            type="button"
            onClick={() => navigate(ROLES_ROUTE)}
            className="w-full sm:w-auto px-6 py-3 border border-base-content/20 text-base-content/70 hover:bg-base-200 rounded-xl font-bold text-sm transition-all"
            disabled={submitting}
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={submitting}
            className="w-full sm:flex-1 btn-premium justify-center"
          >
            {submitting ? (
              <>
                <div className="w-4 h-4 border-2 border-primary-content/30 border-t-primary-content rounded-full animate-spin"></div>
                <span>Creating Role...</span>
              </>
            ) : (
              <>
                <PlusCircle size={16} />
                <span>Create Role</span>
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default NewRole;